// textStats.js
// Contains basic text metric functions and a placeholder for advanced calculations

// Basic stats
export function getCharacterCount(text) {
  return text.length;
}

export function getCharacterCountNoSpaces(text) {
  return text.replace(/\s+/g, '').length;
}

export function getWordCount(text) {
  if (!text.trim()) return 0;
  // Splitting on whitespace is a rough approach
  return text.trim().split(/\s+/).length;
}

export function getSentenceCount(text) {
  if (!text.trim()) return 0;
  // Splitting on punctuation followed by a space or end
  const sentences = text.split(/[.!?]+/).filter(Boolean);
  return sentences.length;
}

export function getParagraphCount(text) {
  if (!text.trim()) return 0;
  const paragraphs = text.trim().split(/\n+/).filter(Boolean);
  return paragraphs.length;
}

// Placeholder for advanced metrics

// Approximate reading time, given ~200 words per minute
export function getReadingTime(text) {
  const words = getWordCount(text);
  const minutes = words / 200;
  return minutes;
}

/**
 * Approximate syllable counting
 * Rough approach: count vowel groups in each word, subtract common silent 'e'
 */
function countSyllables(word) {
  if (!word) return 0;
  let lower = word.toLowerCase();
  // remove trailing e (sometimes silent)
  lower = lower.replace(/e\b/, '');
  // count groups of vowels
  const matches = lower.match(/[aeiouy]+/g);
  return matches ? matches.length : 0;
}

/**
 * Estimate total syllables in a string
 */
export function getTotalSyllables(text) {
  const words = text.trim().split(/\s+/);
  let totalSyllables = 0;
  words.forEach((w) => {
    totalSyllables += countSyllables(w);
  });
  return totalSyllables;
}

/**
 * Basic Flesch Reading Ease Score
 * Formula: 206.835 - (1.015 * averageSentenceLength) - (84.6 * averageSyllablesPerWord)
 */
export function getFleschReadingEase(text) {
  const words = getWordCount(text) || 1;
  const sentences = getSentenceCount(text) || 1;
  const totalSyllables = getTotalSyllables(text) || 1;

  const avgSentenceLength = words / sentences;
  const avgSyllablesPerWord = totalSyllables / words;

  const fleschScore = 206.835 - (1.015 * avgSentenceLength) - (84.6 * avgSyllablesPerWord);
  return fleschScore;
}

/**
 * Flesch-Kincaid Grade Level
 * Formula: (0.39 * averageSentenceLength) + (11.8 * averageSyllablesPerWord) - 15.59
 */
export function getFleschKincaidGrade(text) {
  const words = getWordCount(text) || 1;
  const sentences = getSentenceCount(text) || 1;
  const totalSyllables = getTotalSyllables(text) || 1;

  const avgSentenceLength = words / sentences;
  const avgSyllablesPerWord = totalSyllables / words;

  const fkGrade = (0.39 * avgSentenceLength) + (11.8 * avgSyllablesPerWord) - 15.59;
  return fkGrade;
}

/**
 * Count how many words have exactly 1 syllable.
 */
export function getMonosyllabicWordCount(text) {
  const words = text.trim().split(/\s+/);
  let count = 0;
  words.forEach((w) => {
    if (countSyllables(w) === 1) {
      count++;
    }
  });
  return count;
}

/**
 * Count how many words have 3 or more syllables (often considered "polysyllabic").
 */
export function getPolysyllabicWordCount(text) {
  const words = text.trim().split(/\s+/);
  let count = 0;
  words.forEach((w) => {
    if (countSyllables(w) >= 3) {
      count++;
    }
  });
  return count;
}

/**
 * Average word length in characters (excluding spaces).
 */
export function getAverageWordLength(text) {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return 0;
  const totalLength = words.reduce((acc, w) => acc + w.length, 0);
  return totalLength / words.length;
}

/**
 * Average sentence length in words.
 */
export function getAverageSentenceLength(text) {
  const sCount = getSentenceCount(text) || 1;
  const wCount = getWordCount(text);
  return wCount / sCount;
}

/**
 * Count number of space characters (including newlines, tabs, etc. if you want to handle them).
 */
export function getSpaceCount(text) {
  return (text.match(/\s/g) || []).length;
}

/**
 * Syllables per word (if wordCount is zero, return 0).
 */
export function getSyllablesPerWord(text) {
  const totalSyl = getTotalSyllables(text);
  const wCount = getWordCount(text);
  return wCount > 0 ? (totalSyl / wCount) : 0;
}

/* 
Add other advanced metrics here:
  - Flesch Reading Ease
  - Flesch-Kincaid Grade Level
  - Syllable analysis
*/ 